// @file Pinia plugin that listens/triggers changes in the address
import { removeHashidPrefix } from '@@/bits/hashid_helper'
import { useExpandedPostStore } from '@@/pinia/expanded_post'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceShareLinksStore } from '@@/pinia/surface_share_links'

const history = window.history

const addressListener = (): void => {
  const expandedPostStore = useExpandedPostStore()
  const surfaceStore = useSurfaceStore()
  const surfacePosts = useSurfacePostsStore()
  const surfaceShareLinksStore = useSurfaceShareLinksStore()

  const withSearchAndHash = (pathname: string): string => pathname + window.location.search + window.location.hash

  const watchExpandedPost = (state: {
    xExpandedPostTextPanel: boolean
    expandedPostCid: string | null
    shouldFocusExpandedPostAddCommentInput: boolean
  }): void => {
    if (
      !surfacePosts.hasFetchedPosts ||
      surfaceStore.isSlideshow ||
      surfaceStore.isSubmissionRequest ||
      surfaceStore.isGeneratingPDF ||
      surfaceStore.isWhiteboard ||
      surfaceStore.isFieldTrip
    ) {
      return
    }

    let newPathname = ''
    // Fix an issue with browser being able to load URL with multiple slashes
    // e.g. https://padlet.com//about/us
    const originalPathname = window.location.pathname.replace(/(\/)\1+/g, '$1')

    // Systemic Authorization
    // Prioritise hashid if it is present
    if (expandedPostStore.expandedPostHashid != null) {
      if (surfaceShareLinksStore.isBreakout) {
        newPathname = surfaceShareLinksStore.getBreakoutLinkPostPath(
          removeHashidPrefix(expandedPostStore.expandedPostHashid),
        )
      } else {
        newPathname = `/${surfaceStore.canonicalAddress}/wish/${removeHashidPrefix(expandedPostStore.expandedPostHashid)}`
      }

      if (window.location.pathname !== newPathname) {
        history.pushState(
          {
            expandedPostCid: state.expandedPostCid,
            expandedPostHashid: expandedPostStore.expandedPostHashid,
          },
          '',
          withSearchAndHash(newPathname),
        )
      }
    } else if (state.expandedPostCid != null) {
      if (expandedPostStore.expandedPostServerId == null) return
      if (surfaceShareLinksStore.isBreakout) {
        newPathname = surfaceShareLinksStore.getBreakoutLinkPostPath(String(expandedPostStore.expandedPostServerId))
      } else {
        newPathname = `/${surfaceStore.canonicalAddress}/wish/${expandedPostStore.expandedPostServerId}`
      }

      if (window.location.pathname !== newPathname) {
        history.pushState(
          {
            expandedPostCid: state.expandedPostCid,
            expandedPostServerId: expandedPostStore.expandedPostServerId,
          },
          '',
          withSearchAndHash(newPathname),
        )
      }
    } else {
      if (surfaceShareLinksStore.isBreakout) {
        // we're rendering a breakout link
        newPathname = surfaceShareLinksStore.breakoutLinkPath
      } else if (surfaceStore.isEmbedded) {
        newPathname = '/embed/' + surfaceStore.publicKey
      } else {
        newPathname = '/' + surfaceStore.canonicalAddress
      }
      if (originalPathname !== newPathname) {
        const currentSearch = window.location.search
        const currentHash = window.location.hash
        if (history.state?.expandedPostServerId != null) {
          history.pushState({}, '', newPathname + currentSearch + currentHash)
        } else {
          const pathSplited = originalPathname.split('/')
          const expandedPostServerId = pathSplited[pathSplited.length - 1]
          // handle if id is hashed or not
          const expandedPostCid =
            surfacePosts.getPostByServerId(expandedPostServerId)?.cid ??
            surfacePosts.postEntitiesByHashId[expandedPostServerId]?.cid
          history.replaceState(
            { expandedPostCid, expandedPostServerId },
            '',
            originalPathname + currentSearch + currentHash,
          )
          history.pushState({}, '', newPathname + currentSearch + currentHash)
        }
      }
    }
  }

  let unsubscribeFromWatchingExpandedPost = expandedPostStore.$subscribe((_, state) => {
    watchExpandedPost(state)
  })

  window.addEventListener('popstate', (e) => {
    const eventState = e.state
    unsubscribeFromWatchingExpandedPost()
    if (eventState?.expandedPostCid != null) {
      expandedPostStore.expandPost({
        postCid: eventState.expandedPostCid,
        xTextPanel: true,
      })
    } else {
      expandedPostStore.unexpandPost()
    }
    unsubscribeFromWatchingExpandedPost = expandedPostStore.$subscribe((_, state) => {
      watchExpandedPost(state)
    })
  })
}
export { addressListener }
